import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SiteLayout from "@/components/feature/SiteLayout";
import Hero from "./components/Hero";
import Strengths from "./components/Strengths";
import AboutPreview from "./components/AboutPreview";
import LatestProducts from "./components/LatestProducts";
import CtaBanner from "./components/CtaBanner";
import { useSiteInfo } from "@/hooks/useSiteInfo";
import { supabase } from "@/lib/supabase";

interface Notice {
  id: string;
  title: string;
  date: string;
  important: boolean;
}

interface TechDownload {
  id: string;
  title: string;
  category: string;
  file_size: string;
  date: string;
}

interface QualityCard {
  icon: string;
  title: string;
  desc: string;
}

export default function Home() {
  const { t } = useTranslation();
  const site = useSiteInfo();
  const qualityCardsRaw = t("home.quality.cards", { returnObjects: true }) as unknown as QualityCard[];
  const qualityCards = Array.isArray(qualityCardsRaw) ? qualityCardsRaw : [];

  const [notices, setNotices] = useState<Notice[]>([]);
  const [techDownloads, setTechDownloads] = useState<TechDownload[]>([]);

  useEffect(() => {
    supabase
      .from("notices")
      .select("id, title, date, important")
      .order("date", { ascending: false })
      .limit(4)
      .then(({ data }) => { if (data) setNotices(data as Notice[]); });

    supabase
      .from("technical_downloads")
      .select("id, title, category, file_size, date")
      .order("date", { ascending: false })
      .limit(4)
      .then(({ data }) => { if (data) setTechDownloads(data as TechDownload[]); });
  }, []);

  return (
    <SiteLayout navVariant="overlay">
      <h1 className="sr-only">{t("home.seoTitle")}</h1>

      <Hero />
      <Strengths />

      {/* Quick Contact Bar */}
      <section className="py-10 bg-background-100 border-y border-background-200/70">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
            <p className="text-sm font-semibold text-foreground-600 whitespace-nowrap">
              {t("home.quickContact.message")}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a
                href={site.telLink}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line" />
                </span>
                {site.tel}
              </a>
              <a
                href={site.emailLink}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-mail-line" />
                </span>
                {site.email}
              </a>
              <Link
                to="/quotation"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-file-text-line" />
                </span>
                {t("home.quickContact.quoteRequest")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <AboutPreview />

      {/* Quality & Technology Section */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-bold tracking-wider uppercase mb-4">
              {t("home.quality.eyebrow")}
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-extrabold text-foreground-950">
              {t("home.quality.title")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {t("home.quality.desc")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {qualityCards.map((item) => (
              <div
                key={item.title}
                className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center hover:border-primary-200/60 transition-colors"
              >
                <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto">
                  <i className={`${item.icon} text-primary-600 text-lg`} />
                </span>
                <h3 className="mt-4 font-heading font-bold text-sm text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <LatestProducts />

      {/* Recent Notices + Tech Downloads */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
            {/* Notices */}
            <div className="flex-1">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <span className="inline-block text-xs font-bold text-foreground-500 uppercase tracking-wider mb-2">
                    {t("home.noticesTech.noticeEyebrow")}
                  </span>
                  <h3 className="text-xl font-heading font-extrabold text-foreground-950">{t("home.noticesTech.noticeTitle")}</h3>
                </div>
                <Link
                  to="/support"
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer inline-flex items-center gap-1 whitespace-nowrap"
                >
                  {t("home.noticesTech.viewAll")}
                  <i className="ri-arrow-right-line text-[10px]" />
                </Link>
              </div>
              <div className="flex flex-col gap-2">
                {notices.map((notice) => (
                  <Link
                    key={notice.id}
                    to="/support"
                    className="flex items-center justify-between gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {notice.important && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold flex-shrink-0">
                          {t("home.noticesTech.important")}
                        </span>
                      )}
                      <span className="text-sm text-foreground-800 truncate">{notice.title}</span>
                    </div>
                    <span className="text-xs text-foreground-400 flex-shrink-0">{notice.date}</span>
                  </Link>
                ))}
                {notices.length === 0 && (
                  <p className="text-sm text-foreground-400 text-center py-8">{t("home.noticesTech.emptyNotices")}</p>
                )}
              </div>
            </div>

            {/* Tech Downloads */}
            <div className="flex-1">
              <div className="flex items-end justify-between mb-6">
                <div>
                  <span className="inline-block text-xs font-bold text-foreground-500 uppercase tracking-wider mb-2">
                    {t("home.noticesTech.techEyebrow")}
                  </span>
                  <h3 className="text-xl font-heading font-extrabold text-foreground-950">{t("home.noticesTech.techTitle")}</h3>
                </div>
                <Link
                  to="/technical"
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer inline-flex items-center gap-1 whitespace-nowrap"
                >
                  {t("home.noticesTech.viewAll")}
                  <i className="ri-arrow-right-line text-[10px]" />
                </Link>
              </div>
              <div className="flex flex-col gap-2">
                {techDownloads.map((file) => (
                  <Link
                    key={file.id}
                    to="/technical"
                    className="flex items-center justify-between gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-pdf-2-line text-primary-600 text-sm" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-800 truncate">{file.title}</p>
                        <p className="text-xs text-foreground-400 mt-0.5">
                          {file.category} · {file.file_size}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-foreground-400 flex-shrink-0">{file.date}</span>
                  </Link>
                ))}
                {techDownloads.length === 0 && (
                  <p className="text-sm text-foreground-400 text-center py-8">{t("home.noticesTech.emptyTech")}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner />
    </SiteLayout>
  );
}